/**
 * Conversation Detail Page - Chat interface with all messages
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  conversationsApi,
  ConversationDetail,
  Message,
} from '../api/conversations';
import { useWebSocketEvent } from '../hooks/useWebSocket';

// ── Sender styling ──────────────────────────────────────────────────────

type SenderKind = 'client' | 'bot' | 'admin';

interface SenderStyle {
  label: string;
  emoji: string;
  bg: string;
  border: string;
  accent: string;
  text: string;
  avatarBg: string;
  metaColor: string;
  align: 'left' | 'right';
}

const SENDER_STYLES: Record<SenderKind, SenderStyle> = {
  client: {
    label: 'Клієнт',
    emoji: '👤',
    bg: '#ffffff',
    border: '#e8e8e8',
    accent: '#262626',
    text: '#1a1a1a',
    avatarBg: '#f0f0f0',
    metaColor: '#8c8c8c',
    align: 'left',
  },
  admin: {
    label: 'Адмін',
    emoji: '🛠',
    bg: '#1677ff',
    border: '#1677ff',
    accent: '#ffffff',
    text: '#ffffff',
    avatarBg: '#e6f4ff',
    metaColor: 'rgba(255,255,255,0.85)',
    align: 'right',
  },
  bot: {
    label: 'Бот',
    emoji: '🤖',
    bg: '#f6ffed',
    border: '#b7eb8f',
    accent: '#389e0d',
    text: '#1a1a1a',
    avatarBg: '#d9f7be',
    metaColor: '#8c8c8c',
    align: 'right',
  },
};

function getSenderKind(message: Message): SenderKind {
  if (message.sent_by === 'client') return 'client';
  if (message.sent_by === 'system') return 'bot';
  if (message.sent_by.startsWith('admin_')) return 'admin';
  return message.direction === 'inbound' ? 'client' : 'bot';
}

// ── Date helpers ────────────────────────────────────────────────────────

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDateLabel(date: Date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(date, today)) return 'Сьогодні';
  if (isSameDay(date, yesterday)) return 'Вчора';
  const sameYear = date.getFullYear() === today.getFullYear();
  const day = date.getDate();
  const month = MONTHS_UK[date.getMonth()];
  return sameYear
    ? `${day} ${month}`
    : `${day} ${month} ${date.getFullYear()}`;
}

function formatTime(date: Date) {
  return date.toLocaleTimeString('uk-UA', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFullDateTime(date: Date) {
  return date.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ── Component ───────────────────────────────────────────────────────────

export default function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<ConversationDetail | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadConversation = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await conversationsApi.getDetail(id);
      setConversation(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversation();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  useWebSocketEvent('new_message', (event) => {
    if (event.conversation_id === id) {
      setConversation((prev) => {
        if (!prev) return null;
        return { ...prev, messages: [...prev.messages, event.message] };
      });
    }
  });

  useWebSocketEvent('conversation_update', (event) => {
    if (event.conversation_id === id) {
      if (
        event.update_type === 'bot_enabled' ||
        event.update_type === 'bot_disabled'
      ) {
        setConversation((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            bot_enabled: event.update_type === 'bot_enabled',
          };
        });
      }
    }
  });

  const handleSendMessage = async () => {
    if (!id || !messageText.trim() || sending) return;
    try {
      setSending(true);
      const message = await conversationsApi.sendMessage(id, {
        content: messageText.trim(),
      });
      setConversation((prev) => {
        if (!prev) return null;
        return { ...prev, messages: [...prev.messages, message] };
      });
      setMessageText('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Помилка відправки');
    } finally {
      setSending(false);
    }
  };

  const handleToggleBot = async () => {
    if (!id || !conversation) return;
    try {
      await conversationsApi.toggleBot(id, !conversation.bot_enabled);
      setConversation((prev) => {
        if (!prev) return null;
        return { ...prev, bot_enabled: !prev.bot_enabled };
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Помилка зміни статусу бота');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <div style={loadingBoxStyle}>Завантаження...</div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div style={{ padding: 24 }}>
        <div style={errorBoxStyle}>
          <div style={{ color: '#cf1322' }}>
            {error || 'Розмову не знайдено'}
          </div>
          <button
            onClick={() => navigate('/conversations')}
            style={errorLinkStyle}
          >
            Назад до списку
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <button
            onClick={() => navigate('/conversations')}
            style={backBtnStyle}
          >
            ← Назад
          </button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
              {conversation.client_name || 'Невідомий клієнт'}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
              {conversation.channel} • {conversation.channel_user_id}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {conversation.order_id && (
            <button
              onClick={() => navigate(`/orders/${conversation.order_id}`)}
              style={orderBtnStyle}
            >
              Замовлення: {conversation.order_id}
            </button>
          )}
          <button
            onClick={handleToggleBot}
            style={{
              ...toggleBotBtnStyle,
              background: conversation.bot_enabled ? '#fff7e6' : '#e6f4ff',
              color: conversation.bot_enabled ? '#d48806' : '#1677ff',
              border: `1px solid ${conversation.bot_enabled ? '#ffd591' : '#91caff'}`,
            }}
          >
            {conversation.bot_enabled ? '👤 Перехопити' : '🤖 Увімкнути бота'}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={legendStyle}>
        <LegendItem color="#262626" bg="#ffffff" border="#e8e8e8" emoji="👤" label="Клієнт" />
        <LegendItem color="#1677ff" bg="#1677ff" border="#1677ff" emoji="🛠" label="Адмін" textColor="#fff" />
        <LegendItem color="#389e0d" bg="#f6ffed" border="#b7eb8f" emoji="🤖" label="Бот" />
      </div>

      {/* Messages */}
      <div style={messagesContainerStyle}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          {conversation.messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#8c8c8c', padding: 40 }}>
              Повідомлень ще немає
            </div>
          ) : (
            conversation.messages.map((message, idx) => {
              const date = new Date(message.sent_at);
              const prevDate =
                idx > 0
                  ? new Date(conversation.messages[idx - 1].sent_at)
                  : null;
              const showDateSeparator = !prevDate || !isSameDay(prevDate, date);
              const kind = getSenderKind(message);
              const style = SENDER_STYLES[kind];
              const isRight = style.align === 'right';

              return (
                <React.Fragment key={message.id}>
                  {showDateSeparator && (
                    <div style={dateSeparatorStyle}>
                      <div style={dateSeparatorLineStyle} />
                      <span style={dateSeparatorLabelStyle}>
                        {formatDateLabel(date)}
                      </span>
                      <div style={dateSeparatorLineStyle} />
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: isRight ? 'flex-end' : 'flex-start',
                      marginBottom: 10,
                      gap: 8,
                      alignItems: 'flex-end',
                    }}
                  >
                    {!isRight && (
                      <div style={avatarStyle(style.avatarBg)}>
                        {style.emoji}
                      </div>
                    )}

                    <div
                      style={{
                        maxWidth: '70%',
                        minWidth: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isRight ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          marginBottom: 4,
                          paddingLeft: isRight ? 0 : 4,
                          paddingRight: isRight ? 4 : 0,
                          color: '#8c8c8c',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color:
                              kind === 'client'
                                ? '#262626'
                                : kind === 'admin'
                                  ? '#1677ff'
                                  : '#389e0d',
                          }}
                        >
                          {style.label}
                        </span>
                        <span
                          style={{ marginLeft: 8 }}
                          title={formatFullDateTime(date)}
                        >
                          {formatTime(date)}
                        </span>
                      </div>

                      <div
                        style={{
                          background: style.bg,
                          color: style.text,
                          border: `1px solid ${style.border}`,
                          borderRadius: 12,
                          padding: '8px 12px',
                          fontSize: 14,
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        }}
                      >
                        {message.content}
                        {message.attachments && Array.isArray(message.attachments.items) && message.attachments.items.length > 0 && (
                          <div
                            style={{
                              marginTop: 6,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 6,
                            }}
                          >
                            {message.attachments.items.map((att: any, idx: number) => {
                              const url: string = att?.url || '';
                              const isImage = (att?.type === 'image') && url.startsWith('http');
                              const isLink = url.startsWith('http');
                              if (isImage) {
                                return (
                                  <a
                                    key={idx}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'inline-block' }}
                                  >
                                    <img
                                      src={url}
                                      alt={att.filename || 'photo'}
                                      style={{
                                        maxWidth: 240,
                                        maxHeight: 240,
                                        borderRadius: 8,
                                        display: 'block',
                                      }}
                                    />
                                  </a>
                                );
                              }
                              return (
                                <div
                                  key={idx}
                                  style={{ fontSize: 12, color: style.metaColor }}
                                >
                                  📎 {isLink ? (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: style.metaColor }}
                                    >
                                      {att.filename || att.type || 'attachment'}
                                    </a>
                                  ) : (
                                    <span>{att.filename || att.type || 'attachment'} (не завантажено)</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {isRight && (
                      <div style={avatarStyle(style.avatarBg)}>
                        {style.emoji}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div style={inputContainerStyle}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Написати повідомлення..."
              disabled={sending}
              style={inputStyle}
            />
            <button
              onClick={handleSendMessage}
              disabled={!messageText.trim() || sending}
              style={{
                ...sendBtnStyle,
                background:
                  !messageText.trim() || sending ? '#d9d9d9' : '#1677ff',
                cursor:
                  !messageText.trim() || sending ? 'not-allowed' : 'pointer',
              }}
            >
              {sending ? 'Відправка...' : '📤 Відправити'}
            </button>
          </div>
          {!conversation.bot_enabled && (
            <div style={botDisabledNoticeStyle}>
              ⚠️ Бот вимкнений. Ви відповідаєте клієнту вручну.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function LegendItem({
  color,
  bg,
  border,
  emoji,
  label,
  textColor,
}: {
  color: string;
  bg: string;
  border: string;
  emoji: string;
  label: string;
  textColor?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 10,
          background: bg,
          color: textColor || color,
          border: `1px solid ${border}`,
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        <span>{emoji}</span>
        <span>{label}</span>
      </span>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const headerStyle: React.CSSProperties = {
  background: '#fff',
  borderBottom: '1px solid #f0f0f0',
  padding: '12px 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const legendStyle: React.CSSProperties = {
  background: '#fff',
  borderBottom: '1px solid #f0f0f0',
  padding: '8px 24px',
  display: 'flex',
  gap: 12,
  alignItems: 'center',
};

const messagesContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '20px 16px',
};

const inputContainerStyle: React.CSSProperties = {
  background: '#fff',
  borderTop: '1px solid #f0f0f0',
  padding: '12px 24px',
};

const backBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: '#595959',
  cursor: 'pointer',
  fontSize: 14,
  padding: '4px 8px',
};

const orderBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d9d9d9',
  background: '#fff',
  color: '#1677ff',
  cursor: 'pointer',
  fontSize: 13,
};

const toggleBotBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
};

const avatarStyle = (bg: string): React.CSSProperties => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  flexShrink: 0,
});

const dateSeparatorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '20px 0 12px',
  gap: 10,
};

const dateSeparatorLineStyle: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: '#e8e8e8',
};

const dateSeparatorLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#8c8c8c',
  background: '#fff',
  padding: '4px 12px',
  borderRadius: 12,
  border: '1px solid #e8e8e8',
  whiteSpace: 'nowrap',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '10px 14px',
  fontSize: 14,
  borderRadius: 8,
  border: '1px solid #d9d9d9',
  outline: 'none',
};

const sendBtnStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  border: 'none',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
};

const botDisabledNoticeStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: '#d48806',
  background: '#fffbe6',
  border: '1px solid #ffe58f',
  borderRadius: 6,
  padding: '6px 12px',
};

const loadingBoxStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  padding: 40,
  textAlign: 'center',
  color: '#8c8c8c',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#fff2f0',
  border: '1px solid #ffccc7',
  borderRadius: 8,
  padding: 16,
};

const errorLinkStyle: React.CSSProperties = {
  marginTop: 8,
  background: 'transparent',
  border: 'none',
  color: '#cf1322',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
};
